import { useMemo, useState, useCallback, useRef, useEffect } from 'react'
import { useAppStore } from '@forwardflow/store'
import {
   useGetFubStages,
   useGetPeopleTags,
} from '@forwardflow/queries/onboarding.queries'
import {
   useFubPeopleSources,
   useFubPonds,
} from '@forwardflow/queries/fub.queries'
import { TLeadType } from '@forwardflow/types/campaign.types'

interface LeadOption {
   value: string
   label: string
}

interface UseLeadOptionsReturn {
   options: LeadOption[]
   isLoading: boolean
   error: any
   loadMore: () => void
   hasMore: boolean
}

export const useLeadOptions = (
   leadType: TLeadType,
   searchTerm?: string
): UseLeadOptionsReturn => {
   const { currentOrganization } = useAppStore()
   const [page, setPage] = useState(1)
   const [allData, setAllData] = useState<any[]>([])
   const isLoadingRef = useRef(false)
   const lastFetchKey = useRef(`${leadType}-${searchTerm || ''}`)
   const hasInitialData = useRef(false)
   const pageSize = 50

   // Conditionally fetch data based on filter type
   const {
      data: stagesData,
      isLoading: isLoadingStages,
      error: stagesError,
   } = useGetFubStages(
      leadType === 'stage' ? currentOrganization || '' : '',
      pageSize,
      page,
      leadType === 'stage',
      searchTerm
   )

   const {
      data: tagsData,
      isLoading: isLoadingTags,
      error: tagsError,
   } = useGetPeopleTags(
      leadType === 'tags' ? currentOrganization || '' : '',
      page,
      pageSize,
      leadType === 'tags',
      searchTerm
   )

   const {
      data: sourcesData,
      isLoading: isLoadingSources,
      error: sourcesError,
   } = useFubPeopleSources(
      leadType === 'source' ? currentOrganization || '' : '',
      page,
      pageSize,
      leadType === 'source',
      searchTerm
   )

   const {
      data: pondsData,
      isLoading: isLoadingPonds,
      error: pondsError,
   } = useFubPonds(
      leadType === 'assigned_pond_id' ? currentOrganization || '' : '',
      page,
      pageSize,
      leadType === 'assigned_pond_id',
      searchTerm
   )

   // Reset page when search term or leadType changes
   useEffect(() => {
      const newKey = `${leadType}-${searchTerm || ''}`
      if (lastFetchKey.current !== newKey) {
         lastFetchKey.current = newKey
         setPage(1)
         setAllData([])
         isLoadingRef.current = false
         hasInitialData.current = false
      }
   }, [searchTerm, leadType])

   // Accumulate data from current fetch
   useEffect(() => {
      let newData: any[] = []
      let isLoading = false

      switch (leadType) {
         case 'source':
            newData = sourcesData || []
            isLoading = isLoadingSources
            break
         case 'stage':
            newData = stagesData?.data || []
            isLoading = isLoadingStages
            break
         case 'tags':
            newData = tagsData?.data || []
            isLoading = isLoadingTags
            break
         case 'assigned_pond_id':
            newData = pondsData || []
            isLoading = isLoadingPonds
            break
      }

      const currentKey = `${leadType}-${searchTerm || ''}`

      // Only process data if we're not loading and we have new data
      if (!isLoading && newData.length > 0) {
         // If key changed, this is stale data, ignore it
         if (lastFetchKey.current !== currentKey) {
            return
         }

         hasInitialData.current = true

         setAllData((prev) => {
            // If page is 1, replace data, otherwise append
            if (page === 1) return newData

            // Avoid duplicates by checking if data already exists
            const existingIds = new Set(
               prev.map(
                  (item) =>
                     item.leadSource ||
                     item.stageName ||
                     item.tagName ||
                     item.pondId
               )
            )
            const uniqueNewData = newData.filter(
               (item) =>
                  !existingIds.has(
                     item.leadSource ||
                        item.stageName ||
                        item.tagName ||
                        item.pondId
                  )
            )
            return [...prev, ...uniqueNewData]
         })
      } else if (!isLoading && newData.length === 0 && page === 1) {
         // If we got no data on page 1, set empty array
         if (lastFetchKey.current === currentKey) {
            hasInitialData.current = true
            setAllData([])
         }
      }
   }, [
      stagesData,
      tagsData,
      sourcesData,
      pondsData,
      leadType,
      page,
      searchTerm,
      isLoadingStages,
      isLoadingTags,
      isLoadingSources,
      isLoadingPonds,
   ])

   // Track loading state
   const isCurrentlyLoading =
      isLoadingStages || isLoadingTags || isLoadingSources || isLoadingPonds

   // Update ref when loading completes
   useEffect(() => {
      if (!isCurrentlyLoading) {
         isLoadingRef.current = false
      }
   }, [isCurrentlyLoading])

   const loadMore = useCallback(() => {
      // Only load more if not already loading
      if (!isLoadingRef.current) {
         isLoadingRef.current = true
         setPage((p) => p + 1)
      }
   }, [])

   // Check if there's more data to load
   const hasMore = useMemo(() => {
      let currentData: any[] = []
      switch (leadType) {
         case 'source':
            currentData = sourcesData || []
            break
         case 'stage':
            currentData = stagesData?.data || []
            break
         case 'tags':
            currentData = tagsData?.data || []
            break
         case 'assigned_pond_id':
            currentData = pondsData || []
            break
      }
      return currentData.length >= pageSize
   }, [leadType, stagesData, tagsData, sourcesData, pondsData, pageSize])

   // Transform accumulated data to options
   const options = useMemo(() => {
      if (!currentOrganization || !allData || allData.length === 0) return []

      let rawOptions: LeadOption[] = []

      try {
         switch (leadType) {
            case 'source':
               rawOptions = allData
                  .filter((source) => source && source.leadSource)
                  .map((source) => ({
                     value: String(source.leadSource),
                     label: String(source.leadSource),
                  }))
               break
            case 'stage':
               rawOptions = allData
                  .filter((stage) => stage && stage.stageName)
                  .map((stage) => ({
                     value: String(stage.stageName),
                     label: String(stage.stageName),
                  }))
               break
            case 'tags':
               rawOptions = allData
                  .filter((tag) => tag && tag.tagName)
                  .map((tag) => ({
                     value: String(tag.tagName),
                     label: String(tag.tagName),
                  }))
               break
            case 'assigned_pond_id':
               rawOptions = allData
                  .filter((pond) => pond && pond.pondId && pond.pondName)
                  .map((pond) => ({
                     value: String(pond.pondId),
                     label: String(pond.pondName),
                  }))
               break
            default:
               rawOptions = []
         }
      } catch {
         return []
      }

      // Filter out any options with invalid data and remove duplicates
      const validOptions = rawOptions.filter(
         (option) => 
            option && 
            option.value && 
            option.value !== 'undefined' && 
            option.value !== 'null' &&
            option.label && 
            option.label !== 'undefined' && 
            option.label !== 'null'
      )

      // Remove duplicates based on value
      const uniqueOptions = validOptions.filter(
         (option, index, self) =>
            index === self.findIndex((o) => o.value === option.value)
      )

      return uniqueOptions
   }, [leadType, allData, currentOrganization])

   // Determine loading state based on selected filter
   const isLoading = useMemo(() => {
      switch (leadType) {
         case 'source':
            return isLoadingSources
         case 'stage':
            return isLoadingStages
         case 'tags':
            return isLoadingTags
         case 'assigned_pond_id':
            return isLoadingPonds
         default:
            return false
      }
   }, [
      leadType,
      isLoadingSources,
      isLoadingStages,
      isLoadingTags,
      isLoadingPonds,
   ])

   // Determine error state based on selected filter
   const error = useMemo(() => {
      switch (leadType) {
         case 'source':
            return sourcesError
         case 'stage':
            return stagesError
         case 'tags':
            return tagsError
         case 'assigned_pond_id':
            return pondsError
         default:
            return null
      }
   }, [leadType, sourcesError, stagesError, tagsError, pondsError])

   return {
      options,
      isLoading,
      error,
      loadMore,
      hasMore,
   }
}
